import { Button } from '@1trading/react/components/ui/button';
import { Checkbox } from '@1trading/react/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { format, parse } from 'date-fns';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import type { InstitutionAuthorisation, InstitutionAuthorisationOrDefaults } from '@/features/institution/authorisation/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';
import { institutionDetailsAuthorisationSchema } from '@/services/api/profile';

const InstitutionAuthorisationFormForm = ({ defaultValues }: { defaultValues: InstitutionAuthorisationOrDefaults }) => {
  const form = useForm<InstitutionAuthorisation>({
    defaultValues: {
      ...defaultValues,
      fullName: defaultValues?.fullName ?? '',
      date: defaultValues?.date ?? format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
    },
    resolver: standardSchemaResolver(institutionDetailsAuthorisationSchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionAuthorisation) => {
    mutate(
      { authorisation: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/onboarding/verification', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="institution-company" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.authorisation.fullName')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.authorisation.placeholders.fullName')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => {
              const displayDate = field.value ? format(parse(field.value, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", new Date()), 'yyyy-MM-dd HH:mm:ss') : '';

              return (
                <FormItem className="col-span-2">
                  <FormLabel>{t('institutionDetails.authorisation.date')}*</FormLabel>
                  <FormControl>
                    <Input {...field} value={displayDate} placeholder={t('institutionDetails.authorisation.placeholders.date')} readOnly />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="confirmation"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel>{t('institutionDetails.authorisation.confirmation')}</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionAuthorisationFormForm.displayName = 'InstitutionAuthorisationFormForm';

const InstitutionAuthorisationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'authorisation');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate('/institution/onboarding/regulatory-information', { replace: true });
  }, [navigate]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.authorisation ?? {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.authorisation.title')}</PageHeadingTitle>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionAuthorisationFormForm defaultValues={defaultValues} />
      </PageCard>
    </section>
  );
};
InstitutionAuthorisationForm.displayName = 'InstitutionAuthorisationForm';

export default InstitutionAuthorisationForm;
